import React, { useState, useEffect } from 'react';
import { Answer, TQuestions } from './questionnaire-ogd.type';
import { Col, Form, Input, Modal, Radio, Row, Space, RadioChangeEvent, Checkbox } from 'antd';
import Button from 'mixins/ui/button/button';
import PartWrapper from 'mixins/ui/part-wrapper';
import { saveQuestionsResult } from './actions';
import { localize } from '/react4xp/mixins/helpers';
const span = { xxl: 24, xl: 24, lg: 24, md: 24, sm: 24, xs: 24 };

function Questions(props: any) {
    const [formValues, setFormValues] = useState({});
    const [questionsPerPage, setQuestionsPerPage] = useState<number>(5);
    const [currentPage, setCurrentPage] = useState(1);
    useEffect(() => {
        if (props.status === 'PASSED') {
            const formattedAnswers = props.answers.reduce((acc, item) => {
                acc[item.id] =
                    Array.isArray(item.result) && item.result.length === 1
                        ? item.result[0]
                        : item.result;
                return acc;
            }, {});
            setFormValues(formattedAnswers);
        }
    }, [props.status]);
    const onFinish = (values: any) => {
        const arrayOfObjects = Object.entries(formValues).map(([key, value]) => ({
            ogdQuestionId: parseInt(key),
            result: Array.isArray(value) ? value : [value],
        }));
        saveQuestionsResult(
            props.surveyId,
            props.token,
            arrayOfObjects,
            props.language.slice(0, 2).toUpperCase()
        ).then(() => {
            Modal.success({
                content: (
                    <PartWrapper
                        language={props.language}
                        isError={false}
                    >
                        {localize('oprosy-survey-completed-successfully', props.translations)}
                    </PartWrapper>
                ),
                onOk: () => props.setStep('MAIN'),
            });
        });
    };

    const handleReset = () => {
        Modal.info({
            icon: null,
            content: (
                <PartWrapper
                    language={props.language}
                    isError={false}
                >
                    {localize('oprosy-question-want-clear-form', props.translations)}
                </PartWrapper>
            ),
            onOk: () => setFormValues({}),
            onCancel: () => Modal.destroyAll(),
            closable: true,
        });
    };

    const handleInputChange = (questionId, value) => {
        setFormValues((prevFormValues) => ({
            ...prevFormValues,
            [questionId]: value,
        }));
    };

    const handleCheckboxChange = (questionId, values) => {
        setFormValues((prevFormValues) => ({
            ...prevFormValues,
            [questionId]: values,
        }));
    };

    const handleRadioChange = (questionId, value) => {
        setFormValues((prevFormValues) => ({
            ...prevFormValues,
            [questionId]: value,
        }));
    };
    const totalQuestions = props.questions.length;
    const totalPages = Math.ceil(totalQuestions / questionsPerPage);

    const handleNextPage = () => {
        setCurrentPage((prevPage) => prevPage + 1);
    };

    const handlePreviousPage = () => {
        setCurrentPage((prevPage) => prevPage - 1);
    };

    const renderQuestions = () => {
        const startIndex = (currentPage - 1) * questionsPerPage;
        const endIndex = startIndex + questionsPerPage;
        const currentPageQuestions = props.questions.slice(startIndex, endIndex);
        return currentPageQuestions.map((item: TQuestions, index: number) => (
            <div
                key={`${item.id}_${index}`}
                className="mt-3"
            >
                <Col {...span}>
                    <Form.Item
                        className="questions"
                        initialValue={formValues[index]}
                        label={
                            (currentPage - 1) * questionsPerPage + index + 1 + '. ' + item.question
                        }
                        name={item.id}
                        rules={[
                            {
                                required: item.requiredAns,
                                message: localize('error.field-is-required', props.commonTranslations),
                            },
                        ]}
                    >
                        <Space direction="horizontal">
                            {((item.format === 'Выбор вариантов' && !item.isMultiSelect) ||
                                item.format === 'Да/Нет') && (
                                <Radio.Group
                                    key={`${item.id}_${index}_${item.format}`}
                                    onChange={(e: RadioChangeEvent) =>
                                        handleRadioChange(item.id, e.target.value)
                                    }
                                    value={formValues[item.id]}
                                    disabled={props.status === 'PASSED' ? true : false}
                                >
                                    {item.answer.map((x: Answer, index) => (
                                        <Radio
                                            checked={formValues[item.id] === x.answer}
                                            key={`${x.id}_${index}_${x.answer}`}
                                            value={x.answer}
                                        >
                                            {x.answer}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            )}
                            {item.isMultiSelect && item.format === 'Выбор вариантов' && (
                                <Checkbox.Group
                                    key={item.id}
                                    onChange={(values: string[]) =>
                                        handleCheckboxChange(item.id, values)
                                    }
                                    value={formValues[item.id] || []}
                                    disabled={props.status === 'PASSED'}
                                >
                                    {item.answer.map((x: Answer, index) => (
                                        <Checkbox
                                            key={item.id + index + x.answer}
                                            value={x.answer}
                                            checked={
                                                formValues[item.id] &&
                                                formValues[item.id]?.includes(x.answer)
                                            }
                                        >
                                            {x.answer}
                                        </Checkbox>
                                    ))}
                                </Checkbox.Group>
                            )}
                            {item.format === 'Свободный ввод' && (
                                <Input
                                    onChange={(e) => handleInputChange(item.id, e.target.value)}
                                    value={formValues[item.id]}
                                    placeholder={localize('placeholder.enter-answer', props.commonTranslations)}
                                    disabled={props.status === 'PASSED' ? true : false}
                                />
                            )}
                        </Space>
                    </Form.Item>
                </Col>
            </div>
        ));
    };
    return (
        <>
            <Form
                onFinish={onFinish}
                layout="vertical"
            >
                {renderQuestions()}

                <hr className="my-3" />
                <Row
                    justify="start"
                    gutter={[16, { md: 16, xs: 16 }]}
                >
                    <Col
                        md={3}
                        xs={3}
                        lg={3}
                    >
                        <Button
                            onClick={handleReset}
                            disabled={props.status === 'PASSED' ? true : false}
                        >
                            {localize('button.close', props.commonTranslations)}
                        </Button>
                    </Col>
                    {currentPage > 1 && (
                        <Col
                            md={3}
                            xs={3}
                            lg={3}
                        >
                            <Button
                                color="blue"
                                onClick={handlePreviousPage}
                            >
                                {localize('button.back', props.commonTranslations)}
                            </Button>
                        </Col>
                    )}
                    {currentPage < totalPages && (
                        <Col
                            md={3}
                            xs={3}
                            lg={3}
                        >
                            <Button
                                color="blue"
                                onClick={handleNextPage}
                            >
                                {localize('button.further', props.commonTranslations)}
                            </Button>
                        </Col>
                    )}
                    <Col
                        offset={
                            props.questions.length <= 5
                                ? 18
                                : currentPage === 1 || currentPage === totalPages
                                ? 15
                                : !(currentPage === 1 || currentPage === totalPages)
                                ? 12
                                : null
                        }
                        md={3}
                        xs={3}
                        lg={3}
                    >
                        {props.status !== 'PASSED' && (
                            <Button
                                htmlType="submit"
                                type="primary"
                                color="gold"
                            >
                                {localize('button.send', props.commonTranslations)}
                            </Button>
                        )}
                    </Col>
                </Row>
            </Form>
        </>
    );
}

export default Questions;
